import React, { useState, useContext, useEffect, useMemo } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import TextInput from "../../../../common/TextInput";
import TextArea from "../../../../common/TextArea";
import Button from "../../../../common/Button";
import Validation from "../../../../common/Validation";
import DetailHeader from "../../components/DetailHeader";
import LicenseErrorModal from "../../components/LicenseErrorModal";
import FormFooter from "../../components/FormFooter";
import TimeTab from "./TimeTab";
import ExpenseTab from "./ExpenseTab";
import ProjectTab from "./ProjectTab";
import ServiceTab from "./ServiceTab";
import { getLicensedRoleTabs } from "../../roleUtils";
import RemoveApprovalPermissionModal from "./RemoveApprovalPermissionModal";
import {
  getCompanyRoleTargets,
  findApprovalTeamImpact,
} from "./approvalCascade";
import "./RoleForm.css";

const ADMIN_SUB_PERMISSION_KEYS = [
  "adminViewLicenses",
  "adminConfigureAccessGroups",
  "adminConfigureApprovalGroups",
  "adminConfigureRoles",
  "adminConfigureUsers",
];

const initialRoleState = {
  name: "",
  description: "",
  enableJobLocationEditing: false,

  // Time Entry fields
  enableTime: false,
  allowMultipleEmployeeEntries: false,
  allowSigningOtherEmployees: false,
  allowCrewSetup: false,
  showMyEmployeesWeeklySummary: false,
  showMyEmployeesWorkerClass: false,
  allowApprovals: false,
  allowAbsenceEntries: false,
  viewAndSignOnly: false,
  showPayType: false,
  payTypeOptions: {
    regular: true, // Always enabled by default
    overtime: false,
    double: false,
    sick: false,
    vacation: false,
    holiday: false,
    bonus: false,
    other: false,
    nontax: false,
  },
  requireTimer: false,
  requireJob: false,
  showWorkersClass: false,
  showDepartment: false,
  showWorkLocation: false,
  showEquipment: false,
  showUnits: false,
  timerAlertMinutes: 0,
  hoursAlertHours: 0,

  // Expense fields
  enableExpense: false,

  // Project Management fields
  enableProjectManagement: false,
  allowFieldLogs: false,

  // Service Management fields
  enableServiceManagement: false,

  // Administration permissions
  enableAdministration: false,
  adminViewLicenses: false,
  adminConfigureAccessGroups: false,
  adminConfigureApprovalGroups: false,
  adminConfigureRoles: false,
  adminConfigureUsers: false,
  // Deliberately NOT in ADMIN_SUB_PERMISSION_KEYS above — it must never be
  // cascaded by "Enable Administration" (handleAdministrationEnabledChange)
  // or count toward the self-lockout "at least one must remain" guard. It
  // has its own, stricter rule: never grantable/removable on your own role
  // at all (see the checkbox's `disabled` below and api_identity's backend
  // `subPerms` guard in src/routes/user/role.js).
  adminManageAch: false,
};

export default function RoleForm({ role, onSave, onCancel }) {
  const {
    createRole,
    updateRole,
    bulkUpdateRoles,
    saveRoleWithCompanyAssignments,
    updateApprovalTeam,
    deleteApprovalTeam,
    companies,
    adminPermissions,
    licenses,
    fetchLicenses,
  } = useContext(FieldConfigurationContext);
  const [formData, setFormData] = useState(
    role ? { ...initialRoleState, ...role } : initialRoleState
  );
  const [activeTab, setActiveTab] = useState("time");

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const moduleTabs = useMemo(() => getLicensedRoleTabs(licenses), [licenses]);
  useEffect(() => {
    if (moduleTabs.length && !moduleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(moduleTabs[0].key);
    }
  }, [moduleTabs, activeTab]);
  const isTabShown = (key) => moduleTabs.some((t) => t.key === key);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [licenseErrors, setLicenseErrors] = useState([]);
  const [licenseErrorDetails, setLicenseErrorDetails] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [success, setSuccess] = useState("");
  // Set while unchecking "Allow Approvals" would empty out one or more
  // Approval Teams: { teamsToDelete, teamsToTrim, teamsByCompany, dataToSave }
  const [approvalCascade, setApprovalCascade] = useState(null);

  const isEditing = !!role;
  const configureRolesData = companies?.configureRolesData || {};

  // AC 6: a Field user accessing this screen via the CE Field app token/URL
  // flow must not be able to disable Administration on their own role.
  const ownRoleUuid = adminPermissions?.data?.ownRoleUuid || null;
  const isEditingOwnRole =
    isEditing &&
    !!ownRoleUuid &&
    (role?.uuid === ownRoleUuid || (role?.uuids || []).includes(ownRoleUuid));

  const manageableUuidsList = Object.keys(configureRolesData);
  const showCompanySection = manageableUuidsList.length > (isEditing ? 0 : 1);

  // Track which companies should be assigned after save (only within manageable companies)
  const [selectedCompanyUuids, setSelectedCompanyUuids] = useState(
    () =>
      role
        ? (role.companyUuids || []).filter((uuid) => configureRolesData[uuid])
        : manageableUuidsList // Default all selected when creating
  );

  const handleCompanyToggle = (uuid) => {
    setSelectedCompanyUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );
  };

  // Handle toggling the Enable Administration parent checkbox (cascades to all sub-permissions)
  const handleAdministrationEnabledChange = (checked) => {
    // AC 6: block a Field user from disabling Administration on their own role.
    if (!checked && isEditingOwnRole) return;

    setFormData((prev) => ({
      ...prev,
      enableAdministration: checked,
      adminViewLicenses: checked,
      adminConfigureAccessGroups: checked,
      adminConfigureApprovalGroups: checked,
      adminConfigureRoles: checked,
      adminConfigureUsers: checked,
    }));
    if (licenseErrors.length > 0) {
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      setShowLicenseModal(false);
    }
    if (error) setError("");
  };

  // Handle input changes
  const handleChange = (field, value) => {
    // AC 6: unchecking every Field Administrator sub-permission locks the
    // logged-in user out of the admin tabs just as effectively as unchecking
    // "Enable Administration" itself — block that too on their own role.
    if (
      isEditingOwnRole &&
      ADMIN_SUB_PERMISSION_KEYS.includes(field) &&
      !value
    ) {
      const remainingChecked = ADMIN_SUB_PERMISSION_KEYS.some(
        (key) => key !== field && formData[key]
      );
      if (!remainingChecked) return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear license errors when making changes
    if (licenseErrors.length > 0) {
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      setShowLicenseModal(false);
    }
    // Clear general error message when making changes
    if (error) {
      setError("");
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Role name is required";
    } else if (formData.name.length > 30) {
      newErrors.name = "Role name must be 30 characters or less";
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = "Description must be 250 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    const confirmed = window.confirm("Are you sure you want to cancel?");
    if (confirmed) {
      onCancel();
    }
  };

  // Runs after the role save succeeds. Returns a warning string describing
  // any failures, or "" if everything succeeded.
  const applyApprovalCascade = async ({
    teamsToDelete,
    teamsToTrim,
    reassignmentChoicesByTeamUuid,
  }) => {
    const results = await Promise.all([
      ...teamsToDelete.map(({ team }) =>
        deleteApprovalTeam(
          team.uuid,
          { new_approval_team_uuid: reassignmentChoicesByTeamUuid[team.uuid] },
          team.company_uuid
        ).then((r) => ({ team, r }))
      ),
      ...teamsToTrim.map(({ team, remainingUuids }) =>
        updateApprovalTeam(
          team.uuid,
          { name: team.name, company_users: remainingUuids },
          team.company_uuid
        ).then((r) => ({ team, r }))
      ),
    ]);

    const failed = results.filter(({ r }) => !r.success);
    if (failed.length === 0) return "";
    return `However, ${failed.length} Approval Team update${
      failed.length === 1 ? "" : "s"
    } failed (${failed
      .map(({ team }) => team.name)
      .join(", ")}) — please review Approval Teams manually.`;
  };

  // The actual role save, unchanged from before except that it now optionally
  // runs the approval-team cascade after a successful save.
  const performRoleSave = async (dataToSave, cascade) => {
    setIsSaving(true);
    setError("");
    setLicenseErrors([]);
    setLicenseErrorDetails([]);
    setSuccess("");

    try {
      const result = isEditing
        ? manageableUuidsList.length > 0
          ? await saveRoleWithCompanyAssignments(
              role,
              dataToSave,
              selectedCompanyUuids,
              manageableUuidsList,
              companies.data
            )
          : role.uuids?.length > 1
          ? await bulkUpdateRoles(role.uuids, dataToSave)
          : await updateRole(role.uuid, dataToSave)
        : showCompanySection
        ? await saveRoleWithCompanyAssignments(
            { companyUuids: [], uuids: [] },
            dataToSave,
            selectedCompanyUuids,
            manageableUuidsList,
            companies.data
          )
        : await createRole(dataToSave);

      if (result.success) {
        const cascadeWarning = cascade
          ? await applyApprovalCascade(cascade)
          : "";
        setSuccess(
          `Role ${isEditing ? "updated" : "created"} successfully!${
            cascadeWarning ? " " + cascadeWarning : ""
          }`
        );
        // Skip the auto-navigate when a cascade warning needs to stay
        // visible — the user should see it before leaving this screen.
        if (!cascadeWarning) {
          setTimeout(() => {
            onSave && onSave(result.data);
          }, 1000);
        }
      } else if (
        result.status === 403 &&
        result.errors &&
        result.errors.length > 0
      ) {
        // Handle license limit errors with modal
        setError(
          result.error ||
            "Insufficient license seats to enable requested features."
        );
        setLicenseErrors(result.errors);
        setLicenseErrorDetails(result.details || []);
        setShowLicenseModal(true);
      } else {
        setError(
          result.error ||
            `Failed to ${
              isEditing ? "update" : "create"
            } role. Please try again.`
        );
      }
    } catch (err) {
      setError(`Network error. Please check your connection and try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCascadeCancel = () => {
    setApprovalCascade(null);
    // Restore both fields — checking view_and_sign_only is one of the ways
    // allowApprovals gets cleared, so it needs reverting too.
    setFormData((prev) => ({
      ...prev,
      allowApprovals: role.allowApprovals,
      viewAndSignOnly: role.viewAndSignOnly,
    }));
  };

  const handleCascadeConfirm = (reassignmentChoicesByTeamUuid) => {
    const { dataToSave, teamsToDelete, teamsToTrim } = approvalCascade;
    setApprovalCascade(null);
    performRoleSave(dataToSave, {
      teamsToDelete,
      teamsToTrim,
      reassignmentChoicesByTeamUuid,
    });
  };

  // Handle save
  const handleSave = async () => {
    const willDeleteRole =
      isEditing &&
      showCompanySection &&
      selectedCompanyUuids.length === 0 &&
      (role?.companyUuids || []).filter((uuid) => !configureRolesData[uuid])
        .length === 0;

    if (willDeleteRole) {
      const confirmed = window.confirm(
        `Removing all company assignments will permanently delete this role. Are you sure you want to continue?`
      );
      if (!confirmed) return;
    }

    if (!isEditing && showCompanySection && selectedCompanyUuids.length === 0) {
      setError("Please select at least one company to assign this role to.");
      return;
    }

    if (!validate()) {
      setError("Please fix the errors before saving.");
      setSuccess("");
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      return;
    }

    if (
      isEditingOwnRole &&
      (!formData.enableAdministration ||
        ADMIN_SUB_PERMISSION_KEYS.every((key) => !formData[key]))
    ) {
      setError(
        "You cannot remove all Administration access from your own role."
      );
      setSuccess("");
      return;
    }

    // Ensure regular pay type is always enabled
    const dataToSave = {
      ...formData,
      payTypeOptions: {
        ...formData.payTypeOptions,
        regular: true,
      },
    };

    const wasApprovalRemoved =
      isEditing && role?.allowApprovals && !dataToSave.allowApprovals;

    if (wasApprovalRemoved) {
      const companyRoleTargets = getCompanyRoleTargets(
        role,
        selectedCompanyUuids,
        showCompanySection
      );

      const blockedCompanyNames = companyRoleTargets
        .filter(
          ({ companyUuid }) =>
            !companies?.configureApprovalGroupsData?.[companyUuid]
        )
        .map(
          ({ companyUuid }) => companies?.data?.[companyUuid] || companyUuid
        );

      if (blockedCompanyNames.length > 0) {
        setError(
          `You do not have permission to manage Approval Teams in: ${blockedCompanyNames.join(
            ", "
          )}. Cannot remove Allow Approvals there.`
        );
        setSuccess("");
        return;
      }

      setIsSaving(true);
      setError("");
      try {
        const impact = await findApprovalTeamImpact(companyRoleTargets);
        setIsSaving(false);

        // A team losing some (not all) members needs no confirmation —
        // only an empty-team result requires the admin's input.
        if (impact.teamsToDelete.length > 0) {
          setApprovalCascade({ ...impact, dataToSave });
          return;
        }
        if (impact.teamsToTrim.length > 0) {
          await performRoleSave(dataToSave, {
            teamsToDelete: [],
            teamsToTrim: impact.teamsToTrim,
            reassignmentChoicesByTeamUuid: {},
          });
          return;
        }
      } catch (err) {
        setIsSaving(false);
        setError(
          "Failed to check the impact on Approval Teams. Please try again."
        );
        return;
      }
    }

    await performRoleSave(dataToSave);
  };

  return (
    <div className="role-form">
      <DetailHeader
        title={isEditing ? "Edit Role" : "Add New Role"}
        onBack={handleCancel}
        isEditing={false}
      />

      {/* Error and Success Messages */}
      {error && !showLicenseModal && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      {/* License Error Modal */}
      <LicenseErrorModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        roleUuid={role?.uuid}
        errorMessage={error}
        details={licenseErrorDetails}
        context="role"
      />

      {approvalCascade && (
        <RemoveApprovalPermissionModal
          teamsToDelete={approvalCascade.teamsToDelete}
          teamsToTrim={approvalCascade.teamsToTrim}
          teamsByCompany={approvalCascade.teamsByCompany}
          onCancel={handleCascadeCancel}
          onConfirm={handleCascadeConfirm}
        />
      )}

      <div className="role-form__content">
        {/* Basic Role Information */}
        <div className="role-form__section role-form__section--info">
          <h3 className="role-form__section-title">Role Information</h3>
          <div className="role-form__fields">
            <div>
              <TextInput
                label="Role Name"
                required
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter role name (max 30 characters)"
                disabled={isSaving}
              />
              <div className="role-form__char-count">
                {(formData.name || "").length}/30 characters
              </div>
              {errors.name && (
                <div className="role-form__error">{errors.name}</div>
              )}
            </div>
            <div>
              <TextArea
                label="Description"
                value={formData.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter role description (max 250 characters)"
                rows={3}
                disabled={isSaving}
              />
              <div className="role-form__char-count">
                {(formData.description || "").length}/250 characters
              </div>
              {errors.description && (
                <div className="role-form__error">{errors.description}</div>
              )}
            </div>
          </div>
        </div>

        {/* Company Assignments */}
        {showCompanySection && (
          <div className="role-form__section">
            <h3 className="role-form__section-title">Company Assignments</h3>
            <div className="role-form__company-badges">
              {Object.entries(configureRolesData).map(([uuid, name]) => {
                const isSelected = selectedCompanyUuids.includes(uuid);
                const code = companies?.codesMap?.[uuid];
                return (
                  <button
                    key={uuid}
                    type="button"
                    className={`role-form__company-badge${
                      isSelected ? " role-form__company-badge--assigned" : ""
                    }`}
                    onClick={() => handleCompanyToggle(uuid)}
                    disabled={isSaving}
                  >
                    {code != null ? `${name} (${code})` : name}
                  </button>
                );
              })}
            </div>
            {isEditing &&
              selectedCompanyUuids.length === 0 &&
              (role?.companyUuids || []).filter(
                (uuid) => !configureRolesData[uuid]
              ).length === 0 && (
                <div className="role-form__delete-warning">
                  Warning: Removing all company assignments will permanently
                  delete this role.
                </div>
              )}
          </div>
        )}

        {/* General Settings */}
        <div className="role-form__section">
          <h3 className="role-form__section-title">General Settings</h3>
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.enableJobLocationEditing}
              onChange={(e) =>
                handleChange("enableJobLocationEditing", e.target.checked)
              }
              disabled={isSaving}
            />
            <span>Enable Job Location Editing</span>
          </label>
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.enableAdministration}
              onChange={(e) =>
                handleAdministrationEnabledChange(e.target.checked)
              }
              disabled={isSaving || isEditingOwnRole}
            />
            <span>Enable Administration</span>
          </label>
          {isEditingOwnRole && (
            <div className="role-form__delete-warning">
              You cannot disable Administration, or remove all Field
              Administrator permissions, on your own role.
            </div>
          )}
          <div className="role-form__sub-permissions">
            <div className="role-form__sub-permissions-label">
              Field Administrator
            </div>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminViewLicenses}
                onChange={(e) =>
                  handleChange("adminViewLicenses", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>View Licenses</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureAccessGroups}
                onChange={(e) =>
                  handleChange("adminConfigureAccessGroups", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Access Groups</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureApprovalGroups}
                onChange={(e) =>
                  handleChange("adminConfigureApprovalGroups", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Approval Teams</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureRoles}
                onChange={(e) =>
                  handleChange("adminConfigureRoles", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Roles</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureUsers}
                onChange={(e) =>
                  handleChange("adminConfigureUsers", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Users</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminManageAch}
                onChange={(e) =>
                  handleChange("adminManageAch", e.target.checked)
                }
                // Stricter than the other sub-permissions above: this one is
                // never togglable on your own role at all (not just "can't
                // remove the last one") because api_identity's backend
                // forbids granting or removing manage_ach on your own role
                // outright — reflect that here rather than let a save
                // round-trip fail with a 403.
                disabled={
                  isSaving || !formData.enableAdministration || isEditingOwnRole
                }
              />
              <span>Manage ACH Information</span>
            </label>
          </div>
        </div>

        {/* Module Configuration Tabs */}
        <div className="role-form__tabs">
          <div className="role-form__tabs-nav">
            {moduleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`role-form__tab ${
                  activeTab === tab.key ? "role-form__tab--active" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
                disabled={isSaving}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="role-form__tabs-content">
            {isTabShown("time") && activeTab === "time" && (
              <TimeTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
            {isTabShown("expense") && activeTab === "expense" && (
              <ExpenseTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
            {isTabShown("project") && activeTab === "project" && (
              <ProjectTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
            {isTabShown("service") && activeTab === "service" && (
              <ServiceTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
          </div>
        </div>
      </div>
      <FormFooter
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={isSaving}
        saveLabel={isSaving ? "Saving..." : "Save Role"}
      />
    </div>
  );
}
